<div class="roles-container">
  @if (isEditorOpen()) {
    <section class="role-editor" aria-labelledby="role-editor-title">
      <div class="role-editor__content-header">
        <div class="role-editor__title-row">
          <h2 id="role-editor-title">
            {{
              editorState.mode() === 'create'
                ? ('SETTINGS_ACCESS.ROLES.CREATE_TITLE' | translate)
                : ('SETTINGS_ACCESS.ROLES.EDIT_TITLE' | translate)
            }}
          </h2>
          @if (editorState.mode() === 'edit') {
            <span class="role-editor__members">
              {{
                'SETTINGS_ACCESS.ROLES.MEMBER_COUNT'
                  | translate: { count: editorState.sourceRole()?.memberCount ?? 0 }
              }}
            </span>
          }
        </div>

        <div class="role-editor__actions">
          <p-button
            [label]="'SETTINGS_ACCESS.ROLES.REVERT' | translate"
            severity="secondary"
            [text]="true"
            [disabled]="!editorState.hasChanges() || isSaving()"
            (onClick)="discardChanges()" />
        </div>
      </div>

      <tf-settings-inline-message
        [message]="actionError()"
        messageId="roles-editor-error"
        (dismissed)="clearActionError()" />

      <section
        class="role-editor__details"
        [attr.aria-label]="'SETTINGS_ACCESS.ROLES.DETAILS' | translate">
        <div class="role-editor__fields">
          <label class="role-field">
            <span>{{ 'SETTINGS_ACCESS.ROLES.NAME' | translate }}</span>
            <input
              pInputText
              type="text"
              [ngModel]="editorState.name()"
              [disabled]="!canEditCurrentRole() || isSaving()"
              [class.ng-invalid]="!!serverErrors.get('name')"
              [attr.aria-invalid]="serverErrors.get('name') ? true : null"
              [attr.aria-describedby]="serverErrors.get('name') ? 'roleNameError' : null"
              [placeholder]="'SETTINGS_ACCESS.ROLES.NAME_PLACEHOLDER' | translate"
              (ngModelChange)="editorState.name.set($event)" />
            <tf-form-field-error [message]="serverErrors.get('name')" errorId="roleNameError" />
          </label>
          <label class="role-field">
            <span>{{ 'SETTINGS_ACCESS.ROLES.ROLE_DESCRIPTION' | translate }}</span>
            <input
              pInputText
              type="text"
              [ngModel]="editorState.description()"
              [disabled]="!canEditCurrentRole() || isSaving()"
              [class.ng-invalid]="!!serverErrors.get('description')"
              [attr.aria-invalid]="serverErrors.get('description') ? true : null"
              [attr.aria-describedby]="serverErrors.get('description') ? 'roleDescriptionError' : null"
              [placeholder]="'SETTINGS_ACCESS.ROLES.DESCRIPTION_PLACEHOLDER' | translate"
              (ngModelChange)="editorState.description.set($event)" />
            <tf-form-field-error
              [message]="serverErrors.get('description')"
              errorId="roleDescriptionError" />
          </label>
          <div class="role-field role-field--wide">
            <span id="roleAssignableByRolesLabel">
              {{ 'SETTINGS_ACCESS.ROLES.ASSIGNABLE_BY_ROLES' | translate }}
            </span>
            <p-multiSelect
              inputId="roleAssignableByRoles"
              ariaLabelledBy="roleAssignableByRolesLabel"
              [options]="allRoleOptions()"
              optionLabel="label"
              optionValue="value"
              optionDisabled="disabled"
              [ngModel]="editorState.assignableByRoles()"
              [disabled]="!canEditCurrentRole() || isSaving()"
              [invalid]="!!serverErrors.get('assignableByRoles')"
              [attr.aria-describedby]="
                serverErrors.get('assignableByRoles') ? 'roleAssignableByRolesError' : null
              "
              [display]="'chip'"
              [showClear]="true"
              appendTo="body"
              (ngModelChange)="editorState.assignableByRoles.set($event ?? [])" />
            <tf-form-field-error
              [message]="serverErrors.get('assignableByRoles')"
              errorId="roleAssignableByRolesError" />
            <small>{{ 'SETTINGS_ACCESS.ROLES.ASSIGNABILITY_HELP' | translate }}</small>
          </div>
        </div>
      </section>

      <div
        class="role-tabs"
        role="tablist"
        [attr.aria-label]="'SETTINGS_ACCESS.ROLES.TABS' | translate">
        <button
          type="button"
          role="tab"
          [class.active]="editorState.activeTab() === 'permissions'"
          [attr.aria-selected]="editorState.activeTab() === 'permissions'"
          (click)="editorState.activeTab.set('permissions')">
          <i class="pi pi-list" aria-hidden="true"></i>
          {{ 'SETTINGS_ACCESS.ROLES.PERMISSIONS_TAB' | translate }}
        </button>
        <button
          type="button"
          role="tab"
          [class.active]="editorState.activeTab() === 'advanced'"
          [attr.aria-selected]="editorState.activeTab() === 'advanced'"
          (click)="editorState.activeTab.set('advanced')">
          <i class="pi pi-cog" aria-hidden="true"></i>
          {{ 'SETTINGS_ACCESS.ROLES.ADVANCED_TAB' | translate }}
        </button>
      </div>

      @if (editorState.activeTab() === 'permissions') {
        <section class="permission-table" aria-labelledby="permissions-title">
          <div class="permission-table__head" id="permissions-title">
            <span>{{ 'SETTINGS_ACCESS.ROLES.PERMISSION_GROUP' | translate }}</span>
            <span>{{ 'SETTINGS_ACCESS.ROLES.LEVEL' | translate }}</span>
          </div>
          @for (group of permissionGroups; track group.id) {
            <article class="permission-row">
              <div class="permission-row__main">
                <span class="permission-row__icon">
                  <i [class]="group.icon" aria-hidden="true"></i>
                </span>
                <div>
                  <h3>{{ group.titleKey | translate }}</h3>
                  <p>{{ group.descriptionKey | translate }}</p>
                </div>
              </div>

              <div class="permission-row__controls">
                <div
                  class="permission-row__level"
                  [class.permission-row__level--advanced]="isGroupAdvancedConfigured(group)">
                  <tf-settings-role-permission-level-selector
                    [group]="group"
                    [options]="groupLevelOptions(group)"
                    [value]="groupLevelDisplayValue(group)"
                    [ariaLabel]="group.titleKey | translate"
                    [disabled]="!canEditCurrentRole() || isSaving()"
                    (valueChange)="selectGroupLevelValue(group.id, $event)" />
                  @if (isGroupAdvancedConfigured(group)) {
                    <span class="permission-row__advanced-note">
                      {{ 'SETTINGS_ACCESS.ROLES.ADVANCED_CONFIGURED' | translate }}
                    </span>
                  }
                </div>
              </div>
            </article>
          }
        </section>
      } @else {
        <section class="advanced-grants" aria-labelledby="advanced-grants-title">
          <div class="advanced-grants__toolbar">
            <label class="search-field">
              <i class="pi pi-search" aria-hidden="true"></i>
              <span class="sr-only">
                {{ 'SETTINGS_ACCESS.ROLES.ADVANCED_SEARCH_LABEL' | translate }}
              </span>
              <input
                pInputText
                type="search"
                [ngModel]="editorState.advancedSearchQuery()"
                [placeholder]="'SETTINGS_ACCESS.ROLES.ADVANCED_SEARCH_PLACEHOLDER' | translate"
                (ngModelChange)="editorState.advancedSearchQuery.set($event)" />
            </label>
            <p-select
              inputId="advancedGrantFilter"
              [options]="advancedFilterOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="editorState.advancedFilter()"
              (ngModelChange)="editorState.advancedFilter.set($event)" />
            <span id="advanced-grants-title" class="advanced-grants__count">
              {{
                'SETTINGS_ACCESS.ROLES.SHOWING_GRANTS'
                  | translate
                    : {
                        count: editorState.selectedGrantList().length,
                        total: availableGrants().length,
                      }
              }}
            </span>
          </div>

          <div class="advanced-grants__list">
            @for (grantGroup of advancedGrantGroups(); track grantGroup.group.id) {
              <section
                class="advanced-group"
                [attr.aria-labelledby]="'advanced-' + grantGroup.group.id">
                <header class="advanced-group__header">
                  <button
                    type="button"
                    class="advanced-group__toggle"
                    [attr.aria-expanded]="isAdvancedGrantGroupExpanded(grantGroup.group.id)"
                    [attr.aria-controls]="'advanced-panel-' + grantGroup.group.id"
                    (click)="toggleAdvancedGrantGroup(grantGroup.group.id)">
                    <i
                      class="pi"
                      [class.pi-chevron-down]="isAdvancedGrantGroupExpanded(grantGroup.group.id)"
                      [class.pi-chevron-right]="!isAdvancedGrantGroupExpanded(grantGroup.group.id)"
                      aria-hidden="true"></i>
                    <span class="advanced-group__icon">
                      <i [class]="grantGroup.group.icon" aria-hidden="true"></i>
                    </span>
                    <span [id]="'advanced-' + grantGroup.group.id" class="advanced-group__title">
                      {{ grantGroup.group.titleKey | translate }}
                    </span>
                    <span class="advanced-group__description">
                      {{ grantGroup.group.descriptionKey | translate }}
                    </span>
                  </button>
                  <strong>
                    {{
                      'SETTINGS_ACCESS.ROLES.ENABLED_COUNT'
                        | translate
                          : { enabled: grantGroup.enabledCount, total: grantGroup.totalCount }
                    }}
                  </strong>
                </header>

                @if (isAdvancedGrantGroupExpanded(grantGroup.group.id)) {
                  <div [id]="'advanced-panel-' + grantGroup.group.id" class="advanced-group__rows">
                    @for (row of grantGroup.rows; track row.grant) {
                      <label class="grant-row" [class.grant-row--disabled]="!canToggleGrant(row)">
                        <input
                          type="checkbox"
                          [checked]="row.enabled"
                          [disabled]="!canToggleGrant(row)"
                          (change)="toggleAdvancedGrant(row, $any($event.target).checked)" />
                        <span class="grant-row__key">{{ row.grant }}</span>
                        <span class="grant-row__copy">
                          <strong>{{ row.label }}</strong>
                          <span>{{ row.description }}</span>
                        </span>
                        <span
                          class="level-pill"
                          [class.level-pill--read]="row.level === 'read'"
                          [class.level-pill--write]="row.level === 'write'"
                          [class.level-pill--manage]="row.level === 'manage'">
                          {{ grantLevelLabel(row.level) }}
                        </span>
                      </label>
                    }
                  </div>
                }
              </section>
            }
          </div>
        </section>
      }
    </section>
  } @else {
    <section class="roles-list" [attr.aria-label]="'SETTINGS_ACCESS.ROLES.TITLE' | translate">
      <p class="roles-list__description">{{ 'SETTINGS_ACCESS.ROLES.DESCRIPTION' | translate }}</p>

      @if (!hasLoadedOnce() && isLoading() && !initialLoadFailed()) {
        <tf-settings-loading-placeholder [rows]="5" [columns]="4" />
      } @else if (initialLoadFailed() && loadError(); as error) {
        <tf-settings-load-error
          [title]="error"
          [message]="'SETTINGS.LOAD_ERROR.MESSAGE' | translate"
          [details]="loadErrorDetails()"
          [retrying]="isLoading()"
          (retry)="reloadRoles()" />
      } @else {
        <tf-settings-list-header
          [searchQuery]="rolesSearchQuery()"
          [searchPlaceholder]="'SETTINGS_ACCESS.ROLES.SEARCH_PLACEHOLDER' | translate"
          [loading]="isLoading()"
          [refreshTooltip]="'SETTINGS_ACCESS.ROLES.RELOAD_ROLES' | translate"
          (searchChange)="onSearchQueryChange($event)"
          (refresh)="reloadRoles()">
          <p-select
            headerStart
            inputId="roleTypeFilter"
            [options]="roleTypeOptions()"
            optionLabel="label"
            optionValue="value"
            [ngModel]="rolesTypeFilter()"
            [ariaLabel]="'SETTINGS_ACCESS.ROLES.FILTERS.ALL_TYPES' | translate"
            (ngModelChange)="onTypeFilterChange($event)" />
          <p-button
            headerActions
            icon="pi pi-plus"
            [label]="'SETTINGS_ACCESS.ROLES.CREATE_BUTTON' | translate"
            [disabled]="!canCreateRole() || initialLoadFailed()"
            [pTooltip]="canCreateRole() ? '' : ('SETTINGS_ACCESS.ROLES.NO_PERMISSION' | translate)"
            tooltipPosition="bottom"
            (onClick)="openCreateEditor()" />
        </tf-settings-list-header>

        <tf-settings-inline-message
          [message]="actionError()"
          messageId="roles-action-error"
          (dismissed)="clearActionError()" />

        <tf-data-table
          data-testid="cmp-settings-roles-table"
          [columns]="columns()"
          [value]="tableRows()"
          [loading]="false"
          [paginator]="false"
          styleClass="p-datatable-sm roles-data-table"
          emptyMessageKey="SETTINGS_ACCESS.ROLES.NO_ROLES_FOUND">
          <ng-template tfColumnDef="roleInfo" let-role>
            <div class="role-cell">
              <div>
                <strong>{{ role.displayName }}</strong>
                @if (role.isSystemRole) {
                  <span class="system-badge">
                    <i class="pi pi-shield" aria-hidden="true"></i>
                    {{ 'SETTINGS_ACCESS.ROLES.SYSTEM' | translate }}
                  </span>
                }
              </div>
              <p>
                {{
                  role.displayDescription || ('SETTINGS_ACCESS.ROLES.NO_DESCRIPTION' | translate)
                }}
              </p>
            </div>
          </ng-template>

          <ng-template tfColumnDef="permissionScope" let-role>
            <tf-settings-role-permission-scope
              [summaries]="role.groupSummaries"
              [grantCount]="role.grantCount"
              [groupCount]="role.groupCount" />
          </ng-template>

          <ng-template tfColumnDef="actions" let-role>
            <div class="actions-cell">
              <p-button
                icon="pi pi-pencil"
                severity="secondary"
                [text]="true"
                [rounded]="true"
                size="small"
                [disabled]="!canUpdateRole() || !role.editable"
                [ariaLabel]="
                  'SETTINGS_ACCESS.ROLES.EDIT_ROLE_ACTION' | translate: { name: role.displayName }
                "
                [pTooltip]="
                  canUpdateRole() && role.editable
                    ? ('GENERAL.EDIT' | translate)
                    : ('SETTINGS_ACCESS.ROLES.NO_PERMISSION' | translate)
                "
                tooltipPosition="top"
                (onClick)="openEditEditor(role)" />
              <p-button
                icon="pi pi-copy"
                severity="secondary"
                [text]="true"
                [rounded]="true"
                size="small"
                [disabled]="!canCreateRole()"
                [ariaLabel]="
                  'SETTINGS_ACCESS.ROLES.DUPLICATE_ROLE_ACTION'
                    | translate: { name: role.displayName }
                "
                [pTooltip]="'SETTINGS_ACCESS.ROLES.DUPLICATE' | translate"
                tooltipPosition="top"
                (onClick)="duplicateRole(role)" />
              <p-button
                icon="pi pi-trash"
                severity="danger"
                [text]="true"
                [rounded]="true"
                size="small"
                [disabled]="isDeleteDisabled(role)"
                [ariaLabel]="
                  'SETTINGS_ACCESS.ROLES.DELETE_ROLE_ACTION' | translate: { name: role.displayName }
                "
                [pTooltip]="deleteTooltip(role)"
                tooltipPosition="top"
                (onClick)="confirmDeleteRole(role)" />
            </div>
          </ng-template>
        </tf-data-table>
      }
    </section>
  }

  <p-confirmDialog />
</div>
